import { Table, type TableProps } from 'antd';
import { pick } from 'lodash/fp';
import { type Dispatch, type Key, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type BankListDataFragment } from '../../../../api/fragments/BankListData';
import { type BankModuleSpecsFragment } from '../../../../api/fragments/BankModuleSpecs';
import { useListBanksQuery } from '../../../../api/queries/listBanks';
import PaginatedTable, { type PageAction } from '../../../../components/PaginatedTable';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import renderBooleanIcon from '../../../../utilities/renderBooleanIcon';
import useListReducer, { type State } from '../../BankListPage/useListReducer';
import BankModuleEmptyList from './BankModuleEmptyList';

type BankModuleTableInnerProps = {
    dataSource: BankListDataFragment[];
    state: State;
    dispatch: Dispatch<PageAction>;
    total: number;
};

type ModuleDataSource = BankListDataFragment & { key: Key };
type BankTable = TableProps<ModuleDataSource>;

const BankModuleTableInner = ({ dataSource, dispatch, state, total }: BankModuleTableInnerProps) => {
    const { t } = useTranslation(['bankModuleDetails']);
    const navigate = useNavigate();

    const onRow = useCallback<BankTable['onRow']>(
        record => ({
            onClick: () => navigate(`/admin/banks/${record.id}`),
        }),
        [navigate]
    );

    return (
        <PaginatedTable
            dataSource={dataSource}
            dispatch={dispatch}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('bankModuleDetails:tabs.bankList')}
            total={total}
        >
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                title={t('bankModuleDetails:bankModuleTable.columns.displayName')}
            />
            <Table.Column key="order" dataIndex="order" title={t('bankModuleDetails:bankModuleTable.columns.order')} />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                title={t('bankModuleDetails:bankModuleTable.columns.isActive')}
                width={120}
            />
        </PaginatedTable>
    );
};

export type BankModuleTableProps = {
    module: BankModuleSpecsFragment;
    permissions: Pick<PermissionValues, 'hasCreatePermission'>;
};

const BankModuleTable = ({ module, permissions }: BankModuleTableProps) => {
    const [state, dispatch] = useListReducer();
    const { page, pageSize, sort } = state;

    const { data } = useListBanksQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { moduleId: module.id },
        },
    });

    // prepare items as data source
    const dataSource = useMemo(() => (data?.list?.items || []).map(item => ({ ...item, key: item.id })), [data]);

    const total = data?.list?.count || 0;

    return total === 0 ? (
        <BankModuleEmptyList permissions={permissions} />
    ) : (
        <BankModuleTableInner dataSource={dataSource} dispatch={dispatch} state={state} total={total} />
    );
};

export default BankModuleTable;
